/**
 * Reality Check XR — experience enrichment criteria.
 * Gold-standard reference: teamLab (teamlab-superblue) in xr-experiences.js.
 * Use this checklist when adding or updating curated experiences.
 */

/** @typedef {'for-you'|'360-video'|'augmented-reality'|'location-based'|'metaverse'|'virtual-reality'} ExperienceCategory */

export const ENRICHMENT_REFERENCE_ID = 'teamlab-superblue';

/** Fields every curated experience should have before publishing. */
export const CORE_REQUIRED_FIELDS = [
  'id',
  'title',
  'category',
  'categoryChip',
  'description',
  'thumbnail',
  'url',
  'primaryFormat',
  'formatBadge',
  'formatDescriptor',
  'realityCheckPreview',
  'officialDescription',
  'priceType',
  'priceLabel',
  'publishPlatform',
  'alsoAvailableOn',
  'deliveryTechnology',
  'accessSurface',
  'accessDevices',
  'availabilityType',
  'availabilityStatus',
  'availabilityStatusLabel',
  'comfortRating',
  'ageRating',
  'contentType',
  'medium',
  'isLBE',
  'launchInBrowser',
  'supportedDevices',
  'tags',
  'verifiedBy',
  'lastUpdated'
];

/** Full Details + Decision section fields (teamLab tier). */
export const FULL_DETAIL_FIELDS = [
  'sessionStructure',
  'languages',
  'themes',
  'activities',
  'replayability',
  'guidanceLevel',
  'groupSize',
  'bestFor',
  'effortSignal',
  'comePrepared'
];

/** Physical / location-based additions. */
export const PHYSICAL_VENUE_FIELDS = [
  'venueModel',
  'venueName',
  'venueAddress',
  'venueCity',
  'venueCountry',
  'locationCountry',
  'locationCity',
  'venueLat',
  'venueLng',
  'venueHours',
  'recommendedVisitTime',
  'typicalSessionLabel',
  'venueParking',
  'venueTransit',
  'venueAccessibilityEntrance'
];

/** Optional polish fields. */
export const OPTIONAL_FIELDS = [
  'slug',
  'heroImageUrl',
  'galleryUrls',
  'creatorName',
  'creatorId',
  'creatorVerified',
  'creatorExperienceCount',
  'editorialBadge',
  'runtimeMinutes',
  'locomotionTypes',
  'multiplayer',
  'hostPlatform',
  'isPlatformApp'
];

/** Category-specific required extras beyond CORE. */
export const CATEGORY_REQUIREMENTS = {
  'location-based': {
    required: [...PHYSICAL_VENUE_FIELDS.filter((f) => f !== 'recommendedVisitTime' && f !== 'typicalSessionLabel')],
    notes: 'Always include priceLabel with ticket tiers and student/senior discounts when known.'
  },
  'virtual-reality': {
    required: ['venueModel', 'typicalSessionLabel', 'locomotionTypes'],
    notes: 'Set venueModel to at-home unless headset is venue-only.'
  },
  '360-video': {
    required: ['runtimeMinutes', 'typicalSessionLabel'],
    notes: 'Include where to watch in comePrepared (browser, YouTube, headset).'
  },
  'augmented-reality': {
    required: ['typicalSessionLabel'],
    notes: 'If experience requires visiting a place, add full PHYSICAL_VENUE_FIELDS — it will cross-list in Location-Based.'
  },
  metaverse: {
    required: [],
    notes: 'Platform apps: isPlatformApp true. Worlds/events inside a platform: hostPlatform + sessionStructure live-event.'
  },
  'for-you': {
    required: [],
    notes: 'Featured picks should match the enrichment level of their canonical experience.'
  }
};

/** CTA-driving fields — must be accurate for label logic in experienceRenderers.js */
export const CTA_FIELD_GUIDE = {
  getLocation: 'Physical venue + free walk-in (priceType free, not scheduled/ticketed)',
  getTickets: 'Physical venue + (priceType ticket, or scheduled/limited-run, or touring)',
  joinWorld: 'hostPlatform set (world inside VRChat etc.), not isPlatformApp',
  downloadApp: 'native-app delivery on standalone title, or isPlatformApp — including priceType paid',
  launchExperience: 'webxr/webar/browser access surfaces'
};

/** priceLabel examples — never use vague "pricing varies"; use starting-at, ranges, or optional add-ons */
export const PRICE_LABEL_EXAMPLES = {
  ticketed: 'Tickets from $32 weekdays · $39 weekends · Student & senior discounts available',
  freeVenue: 'Free museum admission',
  freeReservation: 'Free admission · timed entry required',
  paidApp: '$29.99 on Meta Quest · Steam · PS VR2',
  freeDigital: 'Free on YouTube',
  freeWithIap: 'Free on Apple App Store · $3.99 optional to remove ads',
  startingAt: 'Feature experiences from $39 · Mini-golf from $15 · Arcade from $10',
  range: 'Immersive films $95–$250 · Concerts from $175',
  subscription: '$9.99/month or $99.99/year · free trial available'
};

export const PRICE_LABEL_RULES = [
  'Never write "pricing varies" or similar vague copy.',
  'Use a base price, "from $X", a numeric range ($95–$250), or optional add-ons with exact amounts.',
  'If uncertain, use a researched range rather than leaving the user to guess.'
];

/**
 * Returns missing core/full-detail fields for a raw experience object.
 * Useful for CI or admin tooling — not used in production UI yet.
 */
export function getEnrichmentGaps(exp, { category = exp?.category } = {}) {
  const missing = [];
  for (const field of CORE_REQUIRED_FIELDS) {
    if (exp[field] == null || exp[field] === '') missing.push(field);
  }
  for (const field of FULL_DETAIL_FIELDS) {
    if (exp[field] == null || (Array.isArray(exp[field]) && exp[field].length === 0)) {
      missing.push(field);
    }
  }
  const catReq = CATEGORY_REQUIREMENTS[category];
  if (catReq) {
    for (const field of catReq.required) {
      if (exp[field] == null || exp[field] === '') missing.push(field);
    }
  }
  if (exp.isPhysicalVenue || exp.venueModel === 'specific-venue' || exp.isLBE) {
    for (const field of PHYSICAL_VENUE_FIELDS) {
      if (exp[field] == null || exp[field] === '') missing.push(field);
    }
  }
  return [...new Set(missing)];
}
